import { lazy, Suspense, useEffect } from "react";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router";
import { AuthProvider, useAuth } from "@hicya/auth-sdk/react";
import * as Sentry from "@sentry/react";

import { RouteLoading, AuthLoading } from "@/components/layout/route-loading";
import { AUTH_CLIENT_ID, AUTH_URL, pkceStorage, REDIRECT_URI, tokenStorage } from "@/auth/config";
import { AppShell } from "@/components/layout/appShell";
import { HabitsProvider, useHabits } from "@/context/habits-context";
import { UndoProvider } from "@/context/undo-context";
import { DatabaseProvider } from "@/db/provider";
import { SyncProvider } from "@/sync/provider";
import { ThemeProvider } from "@/theme/provider";
import { NotFound } from "@/routes/not-found";
import { RouteError } from "@/routes/route-error";
import { Settings } from "@/routes/settings";
import { Today } from "@/routes/today";
import { HabitCreate } from "@/routes/habit-create";
import { HabitEdit } from "@/routes/habit-edit";
import { HabitTemplates } from "@/routes/habit-templates";
import { Developer } from "@/routes/developer";

// Lazy-load stats routes (keeps Recharts out of the daily view bundle)
const LazyStats = lazy(() => import("@/routes/stats").then((m) => ({ default: m.Stats })));
const LazyHabitStats = lazy(() =>
  import("@/routes/habit-stats").then((m) => ({ default: m.HabitStats })),
);

function LazyStatsWrapper() {
  return (
    <Suspense fallback={<RouteLoading />}>
      <LazyStats />
    </Suspense>
  );
}

function LazyHabitStatsWrapper() {
  return (
    <Suspense fallback={<RouteLoading />}>
      <LazyHabitStats />
    </Suspense>
  );
}

const LazyAuthCallback = lazy(() =>
  import("@/routes/auth-callback").then((m) => ({ default: m.AuthCallback })),
);

const router = createBrowserRouter([
  {
    path: "auth/callback",
    element: (
      <Suspense fallback={<AuthLoading />}>
        <LazyAuthCallback />
      </Suspense>
    ),
  },
  {
    element: <AppShell />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Navigate to="/today" replace /> },
      { path: "today", element: <Today /> },
      { path: "stats", element: <LazyStatsWrapper /> },
      { path: "settings", element: <Settings /> },
      { path: "habits/new", element: <HabitCreate /> },
      { path: "habits/new/templates", element: <HabitTemplates /> },
      { path: "habits/:id/edit", element: <HabitEdit /> },
      { path: "habits/:id/stats", element: <LazyHabitStatsWrapper /> },
      { path: "developer", element: <Developer /> },
      { path: "*", element: <NotFound /> },
    ],
  },
]);

function SentryUserSync({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id, email: user.email });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);
  return <>{children}</>;
}

function AppInner() {
  const { undoCompletion } = useHabits();

  return (
    <UndoProvider onUndo={undoCompletion}>
      <RouterProvider router={router} />
    </UndoProvider>
  );
}

export function App() {
  return (
    <ThemeProvider>
      <AuthProvider
        authUrl={AUTH_URL}
        clientId={AUTH_CLIENT_ID}
        redirectUri={REDIRECT_URI}
        storage={tokenStorage}
        pkceStorage={pkceStorage}
      >
        <SentryUserSync>
          <DatabaseProvider>
            <SyncProvider>
              <HabitsProvider>
                <AppInner />
              </HabitsProvider>
            </SyncProvider>
          </DatabaseProvider>
        </SentryUserSync>
      </AuthProvider>
    </ThemeProvider>
  );
}
